<div
  class="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-cover bg-center"
  style="background-image: url('/login_background1.jpg')"
>
  <div class="pointer-events-none absolute inset-0 bg-slate-900/72"></div>

  <div
    class="relative z-10 flex w-full max-w-md flex-col items-center px-4 py-12"
  >
    <img class="mb-8 h-10 w-auto" src="/Aksia_Logo_white.png" alt="Aksia" />

    @if (signedOut()) {
      <p class="max-w-xs text-center text-sm text-white/80">
        You're not signed in. Sign in to one of the Aksia applications, then
        open this page again.
      </p>
    } @else if (status(); as s) {
      <div
        class="w-full rounded-lg bg-white/95 p-5 text-sm text-slate-800 shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
      >
        <h1 class="mb-4 text-base font-semibold">Sign-in security</h1>

        <label class="mb-4 block text-xs text-slate-600">
          Your password (needed to add or remove a sign-in method)
          <input
            class="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm outline-none"
            type="password"
            autocomplete="current-password"
            [value]="password()"
            (input)="password.set(value($event))"
          />
        </label>

        <section class="mb-4 border-t border-slate-200 pt-3">
          <h2 class="mb-1 font-medium">Authenticator app</h2>
          @if (s.authenticatorEnabled) {
            <p class="mb-2 text-xs text-slate-600">
              On. {{ s.recoveryCodesLeft }} recovery codes left.
            </p>
            <div class="flex gap-3 text-xs">
              <button
                type="button"
                class="underline disabled:opacity-50"
                [disabled]="inFlight()"
                (click)="regenerateRecoveryCodes()"
              >
                New recovery codes
              </button>
              <button
                type="button"
                class="text-rose-700 underline disabled:opacity-50"
                [disabled]="inFlight()"
                (click)="removeAuthenticator()"
              >
                Remove
              </button>
            </div>
          } @else if (authenticatorKey(); as key) {
            <p class="mb-1 text-xs text-slate-600">
              Scan the QR code with your authenticator app (or type the key),
              then enter the code it shows.
            </p>
            @if (authenticatorUri(); as uri) {
              <app-qr-code
                class="mb-1 block"
                [value]="uri"
                [size]="160"
                alt="QR code for your authenticator app"
              />
            }
            <p class="mb-1 font-mono tracking-wider select-all">{{ key }}</p>
            @if (authenticatorLink(); as link) {
              <a class="mb-2 inline-block text-xs underline" [href]="link"
                >Open in authenticator app</a
              >
            }
            <div class="flex gap-2">
              <input
                class="w-32 rounded border border-slate-300 px-2 py-1.5 text-center tracking-widest outline-none"
                inputmode="numeric"
                maxlength="6"
                placeholder="123456"
                [value]="authenticatorCode()"
                (input)="authenticatorCode.set(value($event))"
              />
              <button
                type="button"
                class="rounded bg-slate-200 px-3 text-xs disabled:opacity-50"
                [disabled]="inFlight()"
                (click)="finishAuthenticator()"
              >
                Confirm
              </button>
            </div>
          } @else {
            <p class="mb-2 text-xs text-slate-600">Off.</p>
            <button
              type="button"
              class="text-xs underline disabled:opacity-50"
              [disabled]="inFlight()"
              (click)="startAuthenticator()"
            >
              Set up
            </button>
          }

          @if (recoveryCodes(); as codes) {
            <p class="mt-3 text-xs text-slate-600">
              Save these recovery codes; they won't be shown again:
            </p>
            <ul class="mt-1 grid grid-cols-2 gap-x-6 font-mono text-xs">
              @for (c of codes; track c) {
                <li>{{ c }}</li>
              }
            </ul>
          }
        </section>

        @if (s.smsAvailable) {
          <section class="mb-4 border-t border-slate-200 pt-3">
            <h2 class="mb-1 font-medium">Phone for text messages</h2>
            @if (s.phoneNumber && s.phoneNumberConfirmed) {
              <p class="mb-2 text-xs text-slate-600">{{ s.phoneNumber }}</p>
              <button
                type="button"
                class="text-xs text-rose-700 underline disabled:opacity-50"
                [disabled]="inFlight()"
                (click)="removePhone()"
              >
                Remove
              </button>
            }
            <div class="mt-2 flex gap-2">
              <input
                class="w-44 rounded border border-slate-300 px-2 py-1.5 outline-none"
                type="tel"
                placeholder="+302101234567"
                [value]="phoneNumber()"
                (input)="phoneNumber.set(value($event))"
              />
              <button
                type="button"
                class="rounded bg-slate-200 px-3 text-xs disabled:opacity-50"
                [disabled]="inFlight()"
                (click)="startPhone()"
              >
                {{ s.phoneNumberConfirmed ? 'Change' : 'Add' }}
              </button>
            </div>
            @if (phoneCodeSent()) {
              <div class="mt-2 flex gap-2">
                <input
                  class="w-32 rounded border border-slate-300 px-2 py-1.5 text-center tracking-widest outline-none"
                  inputmode="numeric"
                  placeholder="Code"
                  [value]="phoneCode()"
                  (input)="phoneCode.set(value($event))"
                />
                <button
                  type="button"
                  class="rounded bg-slate-200 px-3 text-xs disabled:opacity-50"
                  [disabled]="inFlight()"
                  (click)="confirmPhone()"
                >
                  Confirm
                </button>
              </div>
            }
          </section>
        }

        @if (s.usableMethods.length > 1) {
          <section class="border-t border-slate-200 pt-3">
            <h2 class="mb-1 font-medium">Ask first for</h2>
            <div class="flex flex-wrap gap-3 text-xs">
              @for (m of s.usableMethods; track m) {
                @if (m !== 'RecoveryCode') {
                  <label class="flex items-center gap-1">
                    <input
                      type="radio"
                      name="preferred"
                      [checked]="
                        m === (s.preferredMethod ?? s.usableMethods[0])
                      "
                      (change)="setPreferred(m)"
                    />
                    {{ labels[m] }}
                  </label>
                }
              }
            </div>
          </section>
        }

        @if (returnUrl) {
          <div class="mt-2 border-t border-slate-200 pt-3 text-right">
            <button
              type="button"
              class="rounded bg-slate-800 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700"
              (click)="continueToApp()"
            >
              Continue to the application
            </button>
          </div>
        }
      </div>
    } @else {
      <span
        class="block h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white"
      ></span>
    }
  </div>
</div>
